import { cn } from "@/src/shared/utils/mergeTwClasses";
import { Stack, Typography } from "@mui/material";
import { FieldValues, useController } from "react-hook-form";
import { RadioCardGroupProps } from "../lib/type";
import { RadioCard } from "./RadioCard";
import { validationRules } from "@/src/shared/utils/validationRules";
import { useFieldDebouncedCallback } from "@/src/hooks/useFieldDebouncedCallback";
import { USE_SELECT_FIELD_UPDATE_DELAY } from "@/src/shared/const/delay";
import { useHintCardStore } from "@/src/stores/useHintCardStore";
import { HintArea } from "../../HintArea";
import { useFieldDetailInfoModal } from "@/src/stores/useFieldDetailInfoModal";
import { useShallow } from "zustand/react/shallow";

export const RadioCardGroup = <T extends FieldValues>({
  control,
  name,
  items,
  className,
  label,
  columns = 1,
  style,
  defaultValue,
  required,
  disabled,
  deps,
  onChange: onChangeController,
  globalHint = false,
  ...rest
}: RadioCardGroupProps<T>) => {
  const {
    field: { value, name: fieldName, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: validationRules.RadioCard({ required, disabled, deps }),
  });
  const debouncedUpdateField = useFieldDebouncedCallback(
    name,
    USE_SELECT_FIELD_UPDATE_DELAY,
  );

  const selected =
    (typeof value === "string" ? value : value?.value) ||
    (typeof defaultValue === "string" ? value : (defaultValue as any)?.value);

  const { hints } = useHintCardStore();
  const mobileHint = hints[0];
  const openHintDetailsModal = useFieldDetailInfoModal(
    useShallow((state) => state.open),
  );

  const selectedItem = items.find((item) => item.value === selected);
  const selectedItemHint = selectedItem?.hint?.hintsList?.at(0);

  const isHintShown =
    globalHint &&
    selected &&
    !!selectedItemHint &&
    selectedItemHint?.contentDescription === mobileHint?.contentDescription &&
    !!mobileHint;

  return (
    <Stack className={"flex w-full flex-col items-start gap-2"}>
      {label && <Typography>{label}</Typography>}
      <Stack
        className={cn(
          "grid w-full gap-3 max-md:grid-cols-1",
          { "max-md:grid-cols-3": globalHint },
          className,
        )}
        style={{
          ...style,
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        }}
        {...rest}
      >
        {items.map((item, idx) => (
          <RadioCard
            {...item}
            onClick={(e, value) => {
              onChange(value);
              debouncedUpdateField(value);
              if (onChangeController) {
                onChangeController(e, value?.value);
              }
            }}
            selected={item.value === selected}
            error={!!error}
            name={fieldName}
            key={item.id ?? idx}
            globalHint={globalHint}
          />
        ))}
      </Stack>
      {error && (
        <Typography className={"text-xs font-normal text-red"} component={"p"}>
          {error.message}
        </Typography>
      )}
      {isHintShown && (
        <HintArea
          className={"hidden transition-opacity max-md:flex"}
          hintAreaDetailsButtonText={mobileHint.hintCardDetailsButtonText}
          hintAreaDetailsButton={mobileHint.hintCardDetailsButton}
          onHintAreaDetailsButtonPressed={() =>
            openHintDetailsModal({
              title: mobileHint.hintDetailsTitle || "",
              actions: mobileHint.hintDetailsActions,
              description: mobileHint.hintDetailsDescription || "",
            })
          }
        >
          {mobileHint.contentDescription}
        </HintArea>
      )}
    </Stack>
  );
};
